import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge, Form } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchPartnerApiCalls, fetchPartners } from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("fr", fr);

function ListScreen(props) {
  const [callsData, setCallsData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [partnerId, setPartnerId] = useState("");
  const [successFilter, setSuccessFilter] = useState("");
  const [ipAddress, setIpAddress] = useState("");

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const data = await fetchPartners();
        setPartners(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching partners:", error);
      }
    };
    loadPartners();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (dateDebut) filters.date_from = format(dateDebut, "yyyy-MM-dd");
      if (dateFin) filters.date_to = format(dateFin, "yyyy-MM-dd");
      if (partnerId !== "") filters.partner_token_id = partnerId;
      if (successFilter !== "") filters.success = successFilter;
      if (ipAddress) filters.ip_address = ipAddress;

      const data = await fetchPartnerApiCalls(nbr, searchTerm, filters);
      setCallsData(data.data);
    } catch (error) {
      console.error("Error fetching partner API calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1);
  };

  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm, dateDebut, dateFin, partnerId, successFilter, ipAddress]);

  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      {
        Header: "Partenaire",
        accessor: "partner",
        Cell: ({ row }) => {
          const partner = row.original.partner;
          if (partner) return partner.name;
          // Aucun partenaire résolu = token absent ou invalide. L'empreinte permet
          // de reconnaître un même mauvais token réutilisé.
          const fingerprint = row.original.token_fingerprint;
          return (
            <span className="text-muted">
              {fingerprint ? <>Token inconnu <code>{fingerprint}</code></> : "Aucun token"}
            </span>
          );
        },
      },
      {
        Header: "Appel",
        accessor: "endpoint",
        Cell: ({ row }) => (
          <span>
            <Badge bg="light" text="dark" className="me-1">{row.original.method}</Badge>
            <code>{row.original.endpoint}</code>
          </span>
        ),
      },
      {
        Header: "Statut",
        accessor: "status_code",
        Cell: ({ value }) => {
          const variant =
            value < 300 ? "success" : value === 401 ? "dark" : value === 429 ? "warning" : value < 500 ? "danger" : "danger";
          return <Badge bg={variant}>{value}</Badge>;
        },
      },
      {
        Header: "Motif d'échec",
        accessor: "error_reason",
        Cell: ({ value }) => (
          <div
            style={{ maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={value || ""}
          >
            {value || "—"}
          </div>
        ),
      },
      { Header: "IP", accessor: "ip_address", Cell: ({ value }) => value || "—" },
      {
        Header: "Durée",
        accessor: "duration_ms",
        Cell: ({ value }) => (value !== null && value !== undefined ? `${value} ms` : "—"),
      },
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) => format(new Date(value), "dd-MM-yyyy HH:mm:ss", { locale: fr }),
      },
    ].filter(Boolean);
  }, [nbr]);

  if (!callsData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  return (
    <Container>
      <div className="partnerApiCallsList">
        <p className="h6">Accueil / Fournisseurs / Appels API partenaires</p>

        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Appels API partenaires</h1>
          </Col>
        </Row>

        <SearchBar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Rechercher par endpoint..."
        />

        <Row className="mb-3 align-items-end">
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de début</Form.Label>
              <DatePicker
                selected={dateDebut}
                onChange={(date) => {
                  setDateDebut(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de début"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de fin</Form.Label>
              <DatePicker
                selected={dateFin}
                onChange={(date) => {
                  setDateFin(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de fin"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Partenaire</Form.Label>
              <Form.Select
                value={partnerId}
                onChange={(e) => {
                  setPartnerId(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Tous</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Résultat</Form.Label>
              <Form.Select
                value={successFilter}
                onChange={(e) => {
                  setSuccessFilter(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Tous</option>
                <option value="0">❌ Échecs uniquement</option>
                <option value="1">✅ Succès uniquement</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Adresse IP</Form.Label>
              <Form.Control
                type="text"
                placeholder="Filtrer par IP..."
                value={ipAddress}
                onChange={(e) => {
                  setIpAddress(e.target.value);
                  setNbr(1);
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={callsData?.data}
            link={callsData?.links}
            last_page={callsData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={callsData?.total}
            name="partnerApiCall"
          />
        )}
      </div>
    </Container>
  );
}

export default ListScreen;
